<template>
  <v-container fluid class="pa-0">
    <v-alert v-if="!tco || !tco.current_month" type="info" text>
      Aucune donnée de coût disponible pour ce véhicule pour le moment.
    </v-alert>

    <template v-else>
      <v-row>
        <v-col md="4">
          <v-card elevation="2">
            <v-card-title>Coût total du mois</v-card-title>
            <v-card-subtitle>{{ periodLabel }}</v-card-subtitle>
            <v-card-text>
              <div class="text-right display-1">{{ formatCurrency(tco.current_month.total_cost) }}</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col md="4">
          <v-card elevation="2">
            <v-card-title>Coût/km</v-card-title>
            <v-card-subtitle>{{ tco.current_month.distance_is_estimated ? 'Kilométrage estimé' : 'Kilométrage relevé' }}</v-card-subtitle>
            <v-card-text>
              <div class="text-right display-1">
                {{ tco.current_month.cost_per_km !== null ? formatCurrency(tco.current_month.cost_per_km, 4) + ' / km' : 'N/A' }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col md="4">
          <v-card elevation="2">
            <v-card-title>Coûts variables du mois</v-card-title>
            <v-card-subtitle v-if="tco.budget_variance">Au-delà du loyer contractuel ({{ formatCurrency(tco.budget_variance.contractual_monthly_rent) }})</v-card-subtitle>
            <v-card-text v-if="tco.budget_variance">
              <div class="text-right display-1" :class="varianceColorClass">
                {{ tco.budget_variance.variance >= 0 ? '+' : '' }}{{ formatCurrency(tco.budget_variance.variance) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="6">
          <v-card elevation="2">
            <v-card-title>Répartition mensuelle des coûts</v-card-title>
            <v-card-text>
              <highcharts :options="chartBreakdownOptions" :updateArgs="updateArgs"/>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col md="6">
          <v-card elevation="2">
            <v-card-title>Évolution du TCO</v-card-title>
            <v-card-text>
              <highcharts :options="chartTrendOptions" :updateArgs="updateArgs"/>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
const MONTH_LABELS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
]

// Palette catégorielle validée (CVD + contraste), ordre fixe — jamais recyclée
// par rang. Postes du contrat (bas de pile) puis coûts variables (haut de pile).
const BREAKDOWN_SERIES = [
  { key: 'lease_rent', name: 'Loyer', color: '#2a78d6' },
  { key: 'lease_maintenance', name: 'Entretien (forfait contrat)', color: '#eb6834' },
  { key: 'lease_tires', name: 'Pneumatiques', color: '#1baf7a' },
  { key: 'lease_assistance', name: 'Assistance', color: '#eda100' },
  { key: 'lease_insurance', name: 'Assurance', color: '#e87ba4' },
  { key: 'lease_other', name: 'Autres (contrat)', color: '#008300' },
  { key: 'fuel_cost', name: 'Carburant', color: '#4a3aa7' },
  { key: 'maintenance_realised', name: 'Entretiens hors contrat', color: '#e34948' }
]
const CHART_SURFACE = '#fcfcfb'

export default {
  name: 'AVehicleTco',
  props: {
    tco: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      updateArgs: [true, true, { duration: 1000 }],
      chartColumnOptions: {
        colors: ['#7cb5ec', '#f7a35c', '#90ed7d', '#8085e9'],
        chart: {
          height: '350px',
          backgroundColor: 'transparent'
        },
        title: { text: '' },
        subtitle: { text: '' },
        credits: { enabled: false },
        xAxis: { categories: [], crosshair: true },
        yAxis: { min: 0, title: { text: '' } },
        tooltip: {
          headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
          pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
            '<td style="padding:0"><b>{point.y} €</b></td></tr>',
          footerFormat: '</table>',
          shared: true,
          useHTML: true
        },
        plotOptions: {
          column: { pointPadding: 0.2, borderWidth: 0 }
        },
        series: []
      },
      chartBreakdownOptions: {},
      chartTrendOptions: {}
    }
  },
  computed: {
    periodLabel: function () {
      if (!this.tco || !this.tco.current_month) return ''
      return `${MONTH_LABELS[this.tco.current_month.month - 1]} ${this.tco.current_month.year}`
    },
    varianceColorClass: function () {
      if (!this.tco || !this.tco.budget_variance) return ''
      return this.tco.budget_variance.variance > 0 ? 'red--text' : 'green--text'
    }
  },
  methods: {
    formatCurrency: function (value, decimals = 2) {
      if (value === null || value === undefined) return 'N/A'
      return Number(value).toLocaleString('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + ' €'
    },
    buildCharts: function () {
      if (!this.tco || !this.tco.monthly_trend || !this.tco.monthly_trend.length) return

      const categories = this.tco.monthly_trend.map(row => `${MONTH_LABELS[row.month - 1].substr(0, 3)} ${row.year}`)

      // Ordre de la pile = ordre du tableau : les postes du contrat d'abord
      // (bas de pile), les coûts variables ensuite (haut de pile).
      this.chartBreakdownOptions = Object.assign({}, this.chartColumnOptions)
      this.chartBreakdownOptions.colors = BREAKDOWN_SERIES.map(s => s.color)
      this.chartBreakdownOptions.xAxis = { categories, crosshair: true }
      // reversedStacks vaut true par défaut chez Highcharts (la 1ère série de la
      // pile est dessinée EN HAUT) : sans ce false explicite, l'ordre visuel est
      // l'inverse de l'ordre du tableau BREAKDOWN_SERIES ci-dessus.
      this.chartBreakdownOptions.yAxis = { min: 0, title: { text: 'Coût (€)' }, reversedStacks: false }
      this.chartBreakdownOptions.plotOptions = {
        column: { stacking: 'normal', borderWidth: 2, borderColor: CHART_SURFACE }
      }
      this.chartBreakdownOptions.series = BREAKDOWN_SERIES.map(s => ({
        type: 'column',
        name: s.name,
        data: this.tco.monthly_trend.map(row => row[s.key])
      }))

      this.chartTrendOptions = Object.assign({}, this.chartColumnOptions)
      this.chartTrendOptions.xAxis = { categories, crosshair: true }
      this.chartTrendOptions.yAxis = [
        { title: { text: 'Coût total (€)' } },
        { title: { text: 'Coût/km (€)' }, opposite: true }
      ]
      this.chartTrendOptions.plotOptions = {}
      this.chartTrendOptions.series = [
        { type: 'area', name: 'TCO mensuel', data: this.tco.monthly_trend.map(row => row.total_cost), yAxis: 0 },
        { type: 'line', name: 'Coût/km', data: this.tco.monthly_trend.map(row => row.cost_per_km), yAxis: 1 }
      ]
    }
  },
  watch: {
    tco: {
      immediate: true,
      handler: function () {
        this.buildCharts()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
